/**
 * Data Plane API — unified asset catalog + mount ledger.
 *
 * The "Data Plane" is one row per uploaded artifact (rules.md, reference PDF,
 * supervisor correction, …). Mounts attach an asset to an
 * ``org / tenant / network / pool / [agent]`` partition so the same blob can
 * be reused across surfaces without re-vectorisation. See backend
 * ``api.rest.data_plane`` and ``models.data_plane`` for the wire shapes.
 *
 *   POST   /data-plane/uploads                          → presigned PUT URL
 *   POST   /data-plane/assets                           → register asset (idempotent on contentHash)
 *   GET    /data-plane/assets                           → list catalog (optional ``includeMounts=true``)
 *   GET    /data-plane/assets/{assetId}                 → asset + mounts
 *   PATCH  /data-plane/assets/{assetId}                 → toggle usage (Off / Rules / Reference); optional body ``networkId`` scopes the change to non-ORG mounts for that net only when the asset is reused across nets
 *   DELETE /data-plane/assets/{assetId}                 → purge asset + mounts
 *   GET    /data-plane/assets/{assetId}/mounts          → mounts for one asset
 *   POST   /data-plane/assets/{assetId}/mounts          → attach asset to a target
 *   DELETE /data-plane/assets/{assetId}/mounts          → detach (target in body)
 *   GET    /data-plane/networks/{id}/mounts             → assets visible to a network
 *
 * All routes are gated by Cognito JWT (TokenAuthorizer); writes additionally
 * require ``BUILD_ROLES`` server-side. Front-end never bypasses RBAC — the
 * UI surfaces the 403 verbatim so reviewers can repro permissions issues.
 */
import { getRestApiBearerToken } from './apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

function encodePath(s) {
  return encodeURIComponent(String(s || '').trim());
}

function parseSizeBytes(raw) {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() !== '') {
    const n = Number(raw);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

/**
 * GET/POST bodies use ``size`` (``Asset.to_api``). Some UI reads ``sizeBytes``.
 *
 * @param {Record<string, unknown>|null|undefined} asset
 */
export function normalizeAssetFromApi(asset) {
  if (!asset || typeof asset !== 'object') return asset;
  const fromSize = parseSizeBytes(asset.size);
  const fromBytes = parseSizeBytes(asset.sizeBytes);
  const n = fromBytes ?? fromSize;
  if (n === undefined) return { ...asset };
  return { ...asset, size: n, sizeBytes: n };
}

function normalizeNetworkMountsPayload(data) {
  if (!data || typeof data !== 'object') return data;
  const out = { ...data };
  if (out.assets && typeof out.assets === 'object' && !Array.isArray(out.assets)) {
    const next = {};
    for (const [k, v] of Object.entries(out.assets)) {
      next[k] = normalizeAssetFromApi(v);
    }
    out.assets = next;
  }
  const mapMounts = (arr) =>
    Array.isArray(arr)
      ? arr.map((m) => {
          if (!m || typeof m !== 'object') return m;
          const row = { ...m };
          if (row.asset) row.asset = normalizeAssetFromApi(row.asset);
          return row;
        })
      : arr;
  out.networkMounts = mapMounts(out.networkMounts);
  out.orgMounts = mapMounts(out.orgMounts);
  return out;
}

/**
 * @param {string} path
 * @param {RequestInit} [init]
 */
async function authedFetch(path, init = {}) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.error || data.message || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/**
 * Step 1 of the upload flow — ask the server for a presigned PUT URL on the
 * canonical ``tenants/{TN-…}/data-plane/...`` key. The server short-circuits
 * to an existing ``assetId`` when ``contentHash`` is already known, so the
 * follow-up ``createAsset`` call is a free no-op for duplicates.
 *
 * @param {{
 *   fileName: string,
 *   contentType?: string,
 *   contentHash?: string,    // sha256:hex — compute with computeFileSha256
 *   size?: number,
 * }} body
 * @returns {Promise<{
 *   uploadUrl: string, method: 'PUT', bucket: string, s3Key: string,
 *   contentType: string, expiresIn: number, assetId: string,
 *   fileName: string, contentHash: string|null, deduped: boolean, tenantId: string,
 * }>}
 */
export async function createUploadUrl(body) {
  if (!body || !body.fileName) throw new Error('body.fileName is required');
  return authedFetch('/data-plane/uploads', {
    method: 'POST',
    body: JSON.stringify({
      fileName: body.fileName,
      contentType: body.contentType,
      contentHash: body.contentHash,
      size: body.size,
    }),
  });
}

/**
 * Step 2 — PUT the file bytes to the presigned URL. Returns the raw Response
 * so callers can inspect ``ok`` / ``status`` directly. Note: this fetch does
 * **not** include our REST API bearer — the URL itself is the credential.
 *
 * The server-side presign stamps ``x-amz-meta-data-plane-{content-hash,asset-id}``
 * onto the canonical object so the Phase 4.2 OrgHashIndex can dedupe Titan
 * embedding by reading metadata via HeadObject. Boto3 SigV4 signs those
 * along with ``Content-Type`` when both are set on the Params, which means
 * the browser PUT must echo them as headers — otherwise the signature won't
 * match and S3 returns 403. Pass ``meta`` alongside ``contentType`` to
 * round-trip them; they are optional for callers that don't need dedupe.
 */
export async function uploadFileToPresignedUrl(uploadUrl, file, contentType, meta = null) {
  const headers = {};
  if (contentType) headers['Content-Type'] = contentType;
  if (meta && typeof meta === 'object') {
    for (const [k, v] of Object.entries(meta)) {
      if (v == null) continue;
      headers[`x-amz-meta-${k}`] = String(v);
    }
  }
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers,
    body: file,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Upload failed (${res.status}): ${text || res.statusText}`);
  }
  return res;
}

/**
 * Step 3 — register the asset row + initial mounts. Idempotent on
 * ``contentHash``: a re-register with the same hash returns the existing
 * ``assetId`` and merges any new mounts.
 *
 * @param {{
 *   fileName: string,
 *   contentHash: string,
 *   size: number,
 *   usageType?: 'RULES'|'REFERENCE'|'OFF',
 *   s3Key?: string,
 *   contentType?: string,
 *   description?: string,
 *   mounts?: import('./dataPlaneTypes').MountTarget[],
 * }} body
 */
export async function createAsset(body) {
  if (!body || !body.fileName) throw new Error('body.fileName is required');
  if (!body.contentHash) throw new Error('body.contentHash is required');
  const usage = body.usageType != null && String(body.usageType).trim() !== ''
    ? body.usageType
    : 'OFF';
  const data = await authedFetch('/data-plane/assets', {
    method: 'POST',
    body: JSON.stringify({ ...body, usageType: usage }),
  });
  if (data && typeof data === 'object' && data.asset) {
    return { ...data, asset: normalizeAssetFromApi(data.asset) };
  }
  return data;
}

/**
 * @param {{ tenantId?: string, networkId?: string, usageType?: string, q?: string, includeMounts?: boolean }} [filter]
 */
export async function listAssets(filter = {}) {
  const params = new URLSearchParams();
  if (filter.networkId) params.set('networkId', filter.networkId);
  if (filter.usageType) params.set('usageType', filter.usageType);
  if (filter.q) params.set('q', filter.q);
  if (filter.includeMounts) params.set('includeMounts', 'true');
  const qs = params.toString();
  const data = await authedFetch(`/data-plane/assets${qs ? `?${qs}` : ''}`);
  if (data && typeof data === 'object' && Array.isArray(data.assets)) {
    return { ...data, assets: data.assets.map((a) => normalizeAssetFromApi(a)) };
  }
  return data;
}

export async function getAsset(assetId) {
  if (!assetId) throw new Error('assetId is required');
  const data = await authedFetch(`/data-plane/assets/${encodePath(assetId)}`);
  if (data && typeof data === 'object' && data.asset) {
    return { ...data, asset: normalizeAssetFromApi(data.asset) };
  }
  return data;
}

/**
 * PATCH /data-plane/assets/{assetId} — toggle Off / Rules / Reference (parity with Run Data memory slider).
 *
 * @param {string} assetId
 * @param {'RULES'|'REFERENCE'|'OFF'} usageType
 * @param {{ networkId?: string, lane?: 'rules'|'reference'|'RULES'|'REFERENCE',
 *   restrictPoolId?: string,
 *   restrictMountScopes?: ('POOL'|'AGENT_POOL')[],
 *   restrictAgentPoolAgents?: string[],
 * }} [opts]
 *   - ``networkId``: when set, only mounts for this network (non-ORG) change mode — other networks stay intact.
 *   - ``lane``: when set, only mounts whose **current** ``usageType`` matches the lane change — keeps Rules / Reference subscriptions independent on the same asset.
 *   - ``restrictPoolId`` / ``restrictMountScopes`` / ``restrictAgentPoolAgents``: agentic pool hold — limit which mount scopes update (server-side).
 */
export async function patchAssetUsage(assetId, usageType, opts = {}) {
  if (!assetId) throw new Error('assetId is required');
  if (!usageType) throw new Error('usageType is required');
  const body = { usageType };
  const nid = opts?.networkId != null && String(opts.networkId).trim() !== '' ? String(opts.networkId).trim() : '';
  if (nid) body.networkId = nid;
  const laneRaw = opts?.lane != null ? String(opts.lane).trim().toUpperCase() : '';
  if (laneRaw === 'RULES' || laneRaw === 'REFERENCE') body.lane = laneRaw;
  const rpid = opts?.restrictPoolId != null && String(opts.restrictPoolId).trim() !== '' ? String(opts.restrictPoolId).trim() : '';
  if (rpid) body.restrictPoolId = rpid;
  if (Array.isArray(opts?.restrictMountScopes) && opts.restrictMountScopes.length > 0) {
    body.restrictMountScopes = opts.restrictMountScopes.map((s) => String(s || '').trim().toUpperCase()).filter(Boolean);
  }
  if (Array.isArray(opts?.restrictAgentPoolAgents) && opts.restrictAgentPoolAgents.length > 0) {
    body.restrictAgentPoolAgents = opts.restrictAgentPoolAgents.map((s) => String(s || '').trim()).filter(Boolean);
  }
  const data = await authedFetch(`/data-plane/assets/${encodePath(assetId)}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
  if (data && typeof data === 'object' && data.asset) {
    return { ...data, asset: normalizeAssetFromApi(data.asset) };
  }
  return data;
}

export async function deleteAsset(assetId, { poolId } = {}) {
  if (!assetId) throw new Error('assetId is required');
  // §27.0: pass the room the delete is issued from so the server can enforce HOST-ONLY delete
  // (a sibling viewing a <host>-shared reference cannot delete it for everyone).
  const q = poolId ? `?poolId=${encodeURIComponent(poolId)}` : '';
  return authedFetch(`/data-plane/assets/${encodePath(assetId)}${q}`, {
    method: 'DELETE',
  });
}

/**
 * §26.4 — kick the room-uploads consolidation at UPLOAD time (the moment "+" files finish
 * uploading), so the L0/L1/L2 context is built while the user is still typing. The worker publishes
 * a ``CONTEXT_READY`` beacon (correlated by ``stagingId``) when the active tier is ready, which the
 * composer's serial send-gate waits on. Best-effort; the send has a safety timeout regardless.
 *
 * @param {string} networkId
 * @param {{ pool?: string, stagingId?: string, attachments?: Array<{bucket,s3Key,filename}> }} opts
 */
export async function prepareRoomContext(networkId, { pool, stagingId, attachments } = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-context`, {
    method: 'POST',
    body: JSON.stringify({
      action: 'prepare-context',
      pool: pool || undefined,
      stagingId: stagingId || undefined,
      attachments: Array.isArray(attachments) ? attachments : [],
    }),
  });
}

export async function listAssetMounts(assetId) {
  if (!assetId) throw new Error('assetId is required');
  return authedFetch(`/data-plane/assets/${encodePath(assetId)}/mounts`);
}

/**
 * Attach an asset to a partition. ``target`` shape mirrors the backend
 * ``MountTarget`` dataclass:
 *
 *   { scope: 'ORG' }
 *   { scope: 'NETWORK', networkId }
 *   { scope: 'POOL',    networkId, poolId }
 *   { scope: 'AGENT',   networkId, agentName }
 *   { scope: 'AGENT_POOL', networkId, agentName, poolId }
 *
 * @param {string} assetId
 * @param {object} target
 * @param {{ usageType?: 'RULES'|'REFERENCE'|'OFF' }} [opts] When set, stored on the mount row (defaults to the catalog asset's usageType on the server).
 */
export async function addAssetMount(assetId, target, opts = {}) {
  if (!assetId) throw new Error('assetId is required');
  if (!target || !target.scope) throw new Error('target.scope is required');
  const body = { target };
  const ut = opts?.usageType != null && String(opts.usageType).trim() !== '' ? String(opts.usageType).trim() : '';
  if (ut) body.usageType = ut;
  return authedFetch(`/data-plane/assets/${encodePath(assetId)}/mounts`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function removeAssetMount(assetId, target) {
  if (!assetId) throw new Error('assetId is required');
  if (!target || !target.scope) throw new Error('target.scope is required');
  return authedFetch(`/data-plane/assets/${encodePath(assetId)}/mounts`, {
    method: 'DELETE',
    body: JSON.stringify({ target }),
  });
}

/**
 * Read-only rollup for the "Run -> Input Data" / Data Plane network tab —
 * every asset visible to a network (its own mounts plus inherited org-wide
 * mounts).
 *
 * @param {string} networkId
 * @param {{ includeOrg?: boolean }} [opts]
 */
export async function listNetworkMounts(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  const params = new URLSearchParams();
  if (opts.includeOrg === false) params.set('includeOrg', 'false');
  const qs = params.toString();
  const data = await authedFetch(
    `/data-plane/networks/${encodePath(networkId)}/mounts${qs ? `?${qs}` : ''}`,
  );
  return normalizeNetworkMountsPayload(data);
}

/**
 * Generic "✨ Improve with AI" for free-form Context text (the "Write / paste"
 * composer). STATELESS — the server persists nothing; the caller shows the
 * suggestion as accept/reject. Metered under the shared 'refinement' billing
 * row (same as the skill refiner). ``intent`` is an optional hint
 * (``'rules'`` | ``'reference'``) so the model tunes for concision vs. coverage.
 *
 * @param {{ text: string, intent?: 'rules'|'reference'|null }} body
 * @returns {Promise<{
 *   improved: boolean,
 *   original?: string,
 *   suggestion?: { text: string, why: string },
 *   message?: string,
 * }>}
 */
export async function refineText(body) {
  const text = String(body?.text || '').trim();
  if (!text) throw new Error('text is required');
  return authedFetch('/tools/refine', {
    method: 'POST',
    body: JSON.stringify({ text, intent: body?.intent || null }),
  });
}

/**
 * Context Preview — the assembled context **exactly as the LLM receives it** for
 * a given (network, pool, agent). RULES come from the SAME merge the worker runs
 * (faithful, no drift); REFERENCE is shown as placeholders (retrieved at runtime).
 *
 * @param {string} networkId
 * @param {{ pool?: string, agent?: string }} [opts]
 * @returns {Promise<{
 *   networkId: string, poolId: string|null, agentId: string|null,
 *   rules: Array<{ scope: string, label: string, filename: string, contentHash: string, text: string }>,
 *   rulesDropped: Array<{ scope: string, label: string, filename: string, reason: string }>,
 *   reference: Array<{ scope: string, label: string, filenames: string[], sampleChunk?: string }>,
 *   budgetChars: number|null, usedChars: number,
 * }>}
 */
export async function getContextPreview(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  const params = new URLSearchParams();
  if (opts.pool) params.set('pool', opts.pool);
  if (opts.agent) params.set('agent', opts.agent);
  const qs = params.toString();
  return authedFetch(
    `/data-plane/networks/${encodePath(networkId)}/context-preview${qs ? `?${qs}` : ''}`,
  );
}

/**
 * Run a REAL vector retrieval for the View Context panel — the same
 * ``search_memory`` the runtime uses, so the user sees exactly what this agent
 * would pull for a given prompt (chunk text + relevance score + source file).
 * Blank ``query`` → backend uses a neutral probe (representative sample).
 *
 * @param {string} networkId
 * @param {{ pool?: string, agent?: string, query?: string, limit?: number }} opts
 * @returns {Promise<{hits: Array, query: string, usedNeutralProbe: boolean, error: ?string}>}
 */
export async function runContextRetrieval(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(
    `/data-plane/networks/${encodePath(networkId)}/context-retrieval`,
    {
      method: 'POST',
      body: JSON.stringify({
        pool: opts.pool || undefined,
        agent: opts.agent || undefined,
        query: opts.query || '',
        ...(opts.limit ? { limit: opts.limit } : {}),
      }),
    },
  );
}

/**
 * Room Context status — the whole-room view backed by pool memory (the only
 * context dimension that accumulates across every call in a room). Returns the
 * thread roster, the cached synthesis (or null), and a staleness count.
 *
 * @param {string} networkId
 * @param {{ pool?: string }} opts
 * @returns {Promise<{threads: Array, threadCount: number, summary: ?object, newThreadsSince: number}>}
 */
export async function getRoomContext(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  const params = new URLSearchParams();
  if (opts.pool) params.set('pool', opts.pool);
  const qs = params.toString();
  return authedFetch(
    `/data-plane/networks/${encodePath(networkId)}/room-context${qs ? `?${qs}` : ''}`,
  );
}

/**
 * §27 context-tier controls (derive-once reference model). List THIS room's per-(origin, group) tier
 * controls + the instance rooms it can reference. Returns
 * ``{viewerPoolId, controls: [{originPoolId, base, tier}], instanceRooms: [poolId]}``.
 */
export async function listContextTiers(networkId, { pool } = {}) {
  if (!networkId) throw new Error('networkId is required');
  if (!pool) throw new Error('pool is required');
  const qs = new URLSearchParams({ pool }).toString();
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/context-tiers?${qs}`);
}

/** §27 set this room's tier for an origin room's derivation group (tier ∈ L0|L1|L2|OFF). */
export async function setContextTier(networkId, { pool, originPoolId, base, tier }) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/context-tiers`, {
    method: 'POST',
    body: JSON.stringify({ pool, originPoolId, base: base || 'room-uploads', tier }),
  });
}

/** §27 clear this room's control for an origin group → revert to the default (OFF). */
export async function clearContextTier(networkId, { pool, originPoolId, base }) {
  if (!networkId) throw new Error('networkId is required');
  const qs = new URLSearchParams({
    pool, originPoolId, base: base || 'room-uploads',
  }).toString();
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/context-tiers?${qs}`, {
    method: 'DELETE',
  });
}

/**
 * @deprecated RETIRED from the composer (reconcile, 2026-08): the LLM "Suggest next" button was
 * replaced by the DETERMINISTIC roadmap proposal (placeholder + Tab, Approve band) computed on the
 * frontend from the plan ledger (cursor + approvals + waves) — instant, and correct in the
 * draft/approve states where this LLM draft returned "No next step to suggest yet.". The backend
 * route + this client remain for now (no caller) but should NOT be re-wired as a next-step surface;
 * the deterministic path is the single pane. See MILESTONE-SCOPED-PLANNING §next-step + PROJECT-NETWORKS §8.
 *
 * suggest-next (project networks §8): for a PROJECT (phased) room, draft the next-step prompt
 * from the ledger + current milestone. Returns ``{eligible, suggestion, milestoneName,
 * completion, readyTasks}``; ``eligible:false`` for non-project / not-yet-concretized rooms.
 * Read-only + on-demand (one Haiku draft per click).
 * @returns {Promise<{eligible: boolean, suggestion: ?string, milestoneName?: string,
 *   completion?: {done:number,total:number}, readyTasks?: Array}>}
 */
export async function getSuggestedNextPrompt(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  const params = new URLSearchParams();
  if (opts.pool) params.set('pool', opts.pool);
  const qs = params.toString();
  return authedFetch(
    `/data-plane/networks/${encodePath(networkId)}/suggest-next${qs ? `?${qs}` : ''}`,
  );
}

/**
 * PROJECT TASKS (§planner) — the Tasks-tab surface over the canonical ``__planner__`` plan.
 * List the plan (milestone-grouped tasks + the finalized flag + milestone markers).
 *
 * LAZY LOADING (perf) — ``opts.view`` / ``opts.milestone`` keep big plans fast:
 *   - ``{view:'index'}`` → LIGHT tasks (id/milestone/title/status/wave/ready/dependsOn, no heavy
 *     detail/DoD/connectors) + per-milestone ``taskCount``. For shells + the composer suggestion.
 *   - ``{milestone:'M1'|'ARCHITECTURE'}`` → FULL bodies for ONE milestone only (on expand).
 *   - neither → FULL bodies for the whole plan (back-compat).
 * The response echoes ``view`` + ``milestoneFilter`` so callers can cache/merge.
 * @returns {Promise<{eligible:boolean, finalized?:boolean, milestones?:Array, tasks?:Array,
 *   view?:string, milestoneFilter?:string, planningCursor?:string, approvedMilestones?:string[]}>}
 */
/**
 * The live PROJECT MAP (PROJECT-MAP.md) — a derive-on-read single-pane index of the project's
 * goal→milestone→epic→task tree + the deterministic ``gapFacts``. Pass ``{gap:true}`` to also run the
 * bounded LLM sufficiency pass (``gapAnalysis``). ``pool`` is REQUIRED (the §13 project instance scope).
 */
export async function fetchProjectMap(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  const params = new URLSearchParams();
  if (opts.pool) params.set('pool', opts.pool);
  if (opts.gap) params.set('gap', '1');
  if (opts.gate) params.set('gate', '1');           // TIMELINE: the milestone-gated (sign-off) view
  const qs = params.toString();
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/project-map${qs ? `?${qs}` : ''}`);
}

/** ONE GAP AGENT (P2, PLAN-TO-EXECUTE §3) — start the gap agent as a tracked background job (a run already
 *  in flight is returned instead) → ``{gapRun, started}``. */
export async function startGapRun(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  const qs = opts.pool ? `?pool=${encodeURIComponent(opts.pool)}` : '';
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/gap-run${qs}`, {
    method: 'POST',
    body: JSON.stringify(opts.pool ? { poolId: opts.pool } : {}),
  });
}

/** The latest gap-agent job ``{gapRun: {runId, status, step, steps, label, summary, …} | null}``. */
export async function fetchGapRun(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  const qs = opts.pool ? `?pool=${encodeURIComponent(opts.pool)}` : '';
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/gap-run${qs}`);
}

/** NEEDS (G1/G3, GAPS-AND-PREREQUISITES §4) — what the plan needs from a person: ``{ needs, counts }``. */
export async function fetchNeeds(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  const qs = opts.pool ? `?pool=${encodePath(opts.pool)}` : '';
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/needs${qs}`);
}

/** Answer a need: ``{action:'provide', value | connectorRef | uploadKey}`` · ``{action:'not_needed', reason}`` ·
 *  ``{action:'reopen'}``. A refused value comes back as a 400 whose message says why. The answer becomes the
 *  needing agents' PRE-REQ context. Returns ``{ need, context }``. */
export async function answerNeed(networkId, needId, body, opts = {}) {
  if (!networkId || !needId) throw new Error('networkId and needId are required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/needs/${encodePath(needId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ ...(body || {}), ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** CONTEXT MODE (MODES.md §3) — the room discussion: ``{ messages (oldest first), agents: [{id,name}] }``. */
export async function getDiscussion(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  // SCROLL-BACK PAGING: ``limit`` = page size, ``before`` = the oldest shown message's ``sk`` → ``hasMore``
  const q = new URLSearchParams();
  if (opts.pool) q.set('pool', opts.pool);
  if (opts.limit) q.set('limit', String(opts.limit));
  if (opts.before) q.set('before', opts.before);
  const qs = q.toString() ? `?${q.toString()}` : '';
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/discussion${qs}`);
}

/** THE ROOM'S MODE (MODES.md §7) — one mode for everyone in the room: ``{ mode: context|plan|run|verify|null, setBy,
 *  setByName, setAt, canSwitch }`` (null = never set → the journey default). */
export async function getRoomMode(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  const qs = opts.pool ? `?pool=${encodePath(opts.pool)}` : '';
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode${qs}`);
}

/** Switch the room's mode for everyone — room owners/builders (403 otherwise), never while a run is going (409). */
export async function setRoomMode(networkId, mode, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ mode, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** THE ROOM'S MODEL for Context (MODES.md §3e) — the tier every agent answers on in this room (Ask and Do):
 *  'fast' | 'think' | 'reason' | '' (each agent's own). Room OWNER only (403 otherwise). Read via ``getRoomMode``. */
export async function setRoomContextModel(networkId, tier, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ contextModel: tier || '', ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** THE ROOM'S INTERNET SEARCH (MODES.md §10.9) — on by default; the room's run roles remove / add it for everyone.
 *  → ``{internetSearch, internetSearchDefault, internetSearchSetByName, canSetInternet}``. */
export async function setRoomInternet(networkId, on, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ internetSearch: !!on, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** THE ROOM'S STAGE (PRODUCT.md §2) — hold the room at Converse ('converse' = keep conversational) or lift it ('').
 *  The room's run roles only. → ``{stage, stageCap, canSetStageCap, …}``. Read via ``getRoomMode``. */
export async function setRoomStageCap(networkId, cap, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ stageCap: cap || '', ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** EXPERT NETWORKS (EXPERT-NETWORKS.md §5) — plug an Expert Network into this room (``tier: 'room'``) or every room of
 *  the network (``tier: 'network'``, the workspace owner): ``req`` = {expertNetworkId, mode: 'consult'|'gate'|'off'|
 *  'remove', tier}. → ``{expertPlugins, expertCandidates, canSetExperts, canSetNetworkExperts}``. */
export async function setRoomExpert(networkId, req, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ expertPlugin: req || {}, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** THE EXPERT'S FACTS (EXPERT-NETWORKS.md §7b) — in an Expert Network's own room: ``req`` = {action: 'put', kind,
 *  record} | {action: 'delete', kind, key} | {action: 'approve'|'reject', proposalId}. → ``{expertFacts}``. */
export async function setRoomFact(networkId, req, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ fact: req || {}, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** CLIENT FACTS (EXPERT-ROOMS.md §17b) — THIS workspace's own values for a company-wide expert in force in the room
 *  (kept in this workspace, never sent to the expert's): ``req`` = {expertNetworkId, action: 'put', kind, record} |
 *  {expertNetworkId, action: 'delete', kind, key}. → the room's experts view (incl. ``clientFacts``). */
export async function setRoomClientFact(networkId, req, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ clientFact: req || {}, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** CERTIFICATION (EXPERT-ROOMS.md §11, X7) — ``req`` = {checklist: {step: true}, runs: [...], notes?}; the workspace
 *  owner, from the expert team's Lead room. → ``{expertCertification}``. */
export async function setRoomCertification(networkId, req, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ expertCertify: req || {}, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** REQUESTS (EXPERT-ROOMS.md §6, X4) — ``req`` = {action: 'approve'|'reject'|'cancel', expertNetworkId, requestId,
 *  note?}. → ``{request, expertRequests}``. */
export async function setRoomExpertRequest(networkId, req, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ expertRequest: req || {}, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** EXPERT SERVICES (EXPERT-ROOMS.md §5, X3) — an offering's form: ``req`` = {action: 'open'|'save'|'check',
 *  expertNetworkId, offeringId, values?}. → ``{offering, values, errors, complete?}``. */
export async function roomOffering(networkId, req, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ offering: req || {}, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** TEAM ACCESS (EXPERT-ROOMS.md §4, X2) — from an expert team's Lead room: ``req`` = {team, capability, connectionId}
 *  (attach to that department room) or {team, capability, remove: true}. → ``{teamAccess}``. */
export async function setRoomTeamAccess(networkId, req, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ teamAccess: req || {}, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** SETUP PROPOSALS (EXPERT-NETWORKS.md §7c L3) — decide one: ``req`` = {action: 'approve'|'reject', proposalId}.
 *  Approving makes the platform perform it now. → ``{liaisonActions}``. */
export async function setRoomLiaisonAction(networkId, req, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ liaisonAction: req || {}, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** THE NETWORK'S RULEBOOK (EXPERT-NETWORKS.md §12) — the workbench Rules tab. → ``{rulebook: {teams, version, by,
 *  at}, isExpert, agents, proposals, canEdit}``. */
export async function getExpertRules(networkId) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/expert-rules`);
}

/** Edit the rulebook: ``{action: 'apply', ops, version}`` (the version you read — 409 if someone changed it) |
 *  ``{action: 'author', ask}`` (the planner drafts ops; nothing saved) | ``{action: 'proposal', proposalId,
 *  decision: 'accept'|'decline', note?}`` (a room's change proposed to the network). */
export async function setExpertRules(networkId, body) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/expert-rules`, {
    method: 'PUT',
    body: JSON.stringify(body || {}),
  });
}

/** A ROOM ADJUSTS ITS RULES (EXPERT-NETWORKS.md §12) — ``req`` = {action: 'propose', ask} | {action: 'apply',
 *  adjustmentId, keep?: [index]} | {action: 'approve'|'decline', adjustmentId, index} | {action: 'toBase', taskId,
 *  reason?}. → ``{adjustment?, proposal?, ruleChanges}``. */
/** CONSULT (EXPERT-ROOMS.md §13) — ``req`` = {action: 'ask', expertNetworkId, text} | {action: 'cancel', …} (the asking
 *  room) | {action: 'claim'|'send'|'answer'|'reassign', consultId, text?, team?} | {action: 'team'|'trained'|'promote',
 *  team, …} (the expert's rooms). → ``{expertConsults, consult?, trained?}``. */
/** APPROVED INTENTS (EXPERT-ROOMS.md §14d) — ``req`` = {action: 'propose'|'sign'|'signExpert'|'deny'|'run'|'artifact',
 *  intentId?, tenantId?, networkId?, …} → ``{deployIntent?, deployIntents?, artifact?}``. */
export async function setDeployIntent(networkId, req, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ deployIntent: req || {}, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

export async function setExpertConsult(networkId, req, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ expertConsult: req || {}, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** KEEP AS KNOWLEDGE (KNOWLEDGE.md §14, K5) — a room message becomes a PROPOSED item the network's owner reviews:
 *  ``req`` = {kind: 'document'|'procedure'|'qa', text, title?, question?, msgId?} → ``{knowledgeCapture: {…}}``. */
export async function keepAsKnowledge(networkId, req, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ knowledgeCapture: req || {}, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** TRAIN (EXPERT-ROOMS.md §15d) — an expert's DEV room: ``req`` = {action: 'train'} | {action: 'accept'|'dismiss', id}.
 *  → ``{expertTraining: {messages, tokens, status, suggestions, …}}``. */
export async function setExpertTrain(networkId, req, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ expertTrain: req || {}, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

export async function setRoomRuleChange(networkId, req, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ ruleChange: req || {}, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** ZERO-SKILL ONBOARDING (EXPERT-NETWORKS.md §7) — one step: ``req`` = {action: 'answer'|'connect'|'confirm'|
 *  'propose'|'back'|'restart', value?}. → ``{onboarding, expertFacts, composer?}`` (composer = the first request,
 *  pre-filled for the person to send). */
export async function setRoomOnboarding(networkId, req, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ onboarding: req || {}, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** EXPERT TEMPLATES (EXPERT-NETWORKS.md §11) — start this network as an Expert Network from template ``id``. */
export async function applyExpertTemplate(networkId, id, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ expertTemplate: { id }, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** THE PLAN GATE (PRODUCT.md §3) — the room's own milestone plan: ``req`` = {action:'propose', intent} |
 *  {action:'draft', milestones, goal} | {action:'approve', milestones?}. → ``{roomPlan, missing?}``. Run roles only. */
export async function planRoom(networkId, req, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ roomPlan: req || {}, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** THE ROOM WORKSPACE'S HISTORY (PRODUCT.md §4, R3b/R3c) — ``req`` = {action:'approve'|'reject', branch, workspaceId,
 *  workspaceScope} | {action:'publish', workspaceId, workspaceScope} | {action:'target', connectorId, branch, auto}.
 *  Run roles only. */
export async function roomHistoryAction(networkId, req, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-mode`, {
    method: 'PUT',
    body: JSON.stringify({ history: req || {}, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** DIRECT WORK (MODES.md §6) — Context + agent: THAT agent works on it as itself (no engine). ``mode`` ask (read-only,
 *  the default) | do. Returns ``{route:'direct', runId, …}`` (202) or ``{route:'quick', reason, message}`` (a worker
 *  agent, the agent busy, or direct work unavailable — the caller asks the quick answer instead). */
export async function startDirectRun(networkId, { agentId, text, mode = 'ask', attachments = [] } = {}, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/direct-run`, {
    method: 'POST',
    body: JSON.stringify({ agentId, text, mode, ...(attachments && attachments.length ? { attachments } : {}),
      ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** BUILD THE CONTEXT (MODES.md §3, M5) — ``{ status: idle|building, pending: {messages, files}, builtAt, builds,
 *  assets, lastError }``. */
export async function getContextBuild(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  const qs = opts.pool ? `?pool=${encodePath(opts.pool)}` : '';
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/context-build${qs}`);
}

/** Fold what's new in the discussion into the project context (L0 appends, L1 refined, L2 re-told). Async on the
 *  server — ``CONTEXT_BUILT`` says when it lands. 409 while a build runs. */
export async function startContextBuild(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/context-build`, {
    method: 'POST',
    body: JSON.stringify(opts.pool ? { poolId: opts.pool } : {}),
  });
}

/** Post to the room discussion. With ``agentId`` ONLY that agent replies (in its role, one bounded call).
 *  Nothing runs the network. Returns ``{ message, reply?, replyError? }``. */
export async function postDiscussion(networkId, { text, agentId = '', agentIds = [], attachments = [], mentions = [], threadOf = '', threadSk = '' } = {}, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/discussion`, {
    method: 'POST',
    body: JSON.stringify({ text, ...(agentId ? { agentId } : {}), ...(agentIds && agentIds.length > 1 ? { agentIds } : {}),
      ...(attachments && attachments.length ? { attachments } : {}),
      // PEOPLE tagged (MENTIONS.md) — notified, never "asked"
      ...(mentions && mentions.length ? { mentions } : {}), ...(opts.pool ? { poolId: opts.pool } : {}),
      // THREADS (MODES.md §3b): a reply to a ROOT message (one level)
      ...(threadOf ? { threadOf, threadSk } : {}) }),
  });
}

/** THE DOC PANE (CONTEXT-DOCS.md §P1): a short-lived inline URL for ONE file of a message →
 *  ``{ url, fileName, contentType, size, expiresIn }``. */
export async function getDiscussionFileUrl(networkId, m, index, opts = {}) {
  if (!networkId || !m || !m.id) throw new Error('networkId and message are required');
  const q = new URLSearchParams();
  if (opts.pool) q.set('pool', opts.pool);
  q.set('fileOf', m.id);
  q.set('index', String(index || 0));
  if (m.sk) q.set('sk', m.sk);
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/discussion?${q.toString()}`);
}

/** THE DOC PANE'S TEXT TAB (CONTEXT-DOCS.md §P2): exactly what the context reads from one file →
 *  ``{ fileName, status: reading|ready|failed, text, error, pages, ocrPages, truncated }``. */
export async function getDiscussionFileText(networkId, m, index, opts = {}) {
  if (!networkId || !m || !m.id) throw new Error('networkId and message are required');
  const q = new URLSearchParams();
  if (opts.pool) q.set('pool', opts.pool);
  q.set('fileOf', m.id);
  q.set('index', String(index || 0));
  q.set('text', '1');
  if (m.sk) q.set('sk', m.sk);
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/discussion?${q.toString()}`);
}

/** LINK PREVIEWS (CONTEXT-DOCS.md §L) — the preview IMAGE we stored for one link of a message → ``{ url }`` (a short-
 *  lived GET of OUR copy; viewers never call the site), or with ``text: true`` the page text the context reads →
 *  ``{ status, text, url, error }``. */
export async function getDiscussionLink(networkId, m, index, opts = {}) {
  if (!networkId || !m || !m.id) throw new Error('networkId and message are required');
  const q = new URLSearchParams();
  if (opts.pool) q.set('pool', opts.pool);
  q.set('linkOf', m.id);
  q.set('index', String(index || 0));
  if (opts.text) q.set('text', '1');
  if (m.sk) q.set('sk', m.sk);
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/discussion?${q.toString()}`);
}

/** Remove a link's preview (its author / a moderator) — the link and its context stay. → ``{ message }``. */
export async function hideDiscussionLink(networkId, m, index, opts = {}) {
  if (!networkId || !m || !m.id) throw new Error('networkId and message are required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/discussion`, {
    method: 'PATCH',
    body: JSON.stringify({ action: 'hide-link', messageId: m.id, sk: m.sk || '', index, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** ONE CONTEXT VERSION (CONTEXT-DOCS.md §P3) → ``{ version, title, text, active, builtAt, assetId }``. */
export async function getContextVersion(networkId, version, opts = {}) {
  if (!networkId || !version) throw new Error('networkId and version are required');
  const q = new URLSearchParams();
  if (opts.pool) q.set('pool', opts.pool);
  q.set('version', String(version));
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/context-build?${q.toString()}`);
}

/** CONTEXT → WORK / PLAN (CONTEXT-DOCS.md §H). A Do output → the context or the plan pack (explicit, never automatic).
 *  ``target``: 'context' → ``{ message }`` · 'plan' → ``{ pick }``. */
export async function shareDoOutput(networkId, { agentId, path, workspaceId, workspaceScope, target }, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/discussion`, {
    method: 'PATCH',
    body: JSON.stringify({ action: 'share-output', agentId, path, workspaceId, workspaceScope, target,
      ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** Approve a context version (a lead) → ``{ version, approvedBy, approvedAt }``. */
export async function approveContextVersion(networkId, version, opts = {}) {
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/context-build`, {
    method: 'POST', body: JSON.stringify({ action: 'approve', version, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** Hand the APPROVED context to Plan → ``{ handoff }``. ``approve: true`` approves it in the same step. */
export async function handoffContext(networkId, { version, docs, approve = false } = {}, opts = {}) {
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/context-build`, {
    method: 'POST',
    body: JSON.stringify({ action: 'handoff', ...(version ? { version } : {}), ...(Array.isArray(docs) ? { docs } : {}),
      approve: !!approve, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** Take an item back out of the plan pack. */
export async function unpickPlanItem(networkId, pickId, opts = {}) {
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/context-build`, {
    method: 'POST', body: JSON.stringify({ action: 'unpick', pickId, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** THE THREAD PANE (MODES.md §3b): a root + ONLY its replies, newest page first (``before`` = the oldest shown
 *  reply's ``sk``) → ``{ root, messages (oldest first), hasMore }``. */
export async function getThread(networkId, root, opts = {}) {
  if (!networkId || !root || !root.id) throw new Error('networkId and root are required');
  const q = new URLSearchParams();
  if (opts.pool) q.set('pool', opts.pool);
  q.set('threadOf', root.id);
  if (root.sk) q.set('threadSk', root.sk);
  if (opts.limit) q.set('limit', String(opts.limit));
  if (opts.before) q.set('before', opts.before);
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/discussion?${q.toString()}`);
}

/** ADOPT an agent's answer (MODES.md §10.4) — a person makes it the team's decision. ``{message, activity}``;
 *  409 when it's already adopted / not an agent's answer. */
export async function adoptDiscussionMessage(networkId, messageId, opts = {}) {
  if (!networkId || !messageId) throw new Error('networkId and messageId are required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/discussion`, {
    method: 'PATCH',
    body: JSON.stringify({ adopt: messageId, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** EDIT / DELETE a discussion message (MODES.md §3, Slack-style): the author edits; the author or a moderator
 *  deletes (a tombstone). The server enforces who; the change lands live for everyone. → ``{ message }``. */
export async function editDiscussionMessage(networkId, m, text, opts = {}) {
  if (!networkId || !m || !m.id) throw new Error('networkId and message are required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/discussion`, {
    method: 'PATCH',
    body: JSON.stringify({ action: 'edit', messageId: m.id, sk: m.sk || '', text, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** STAND-IN (STAND-IN.md) — MY stand-in in this room: ``{ setting, eligible, drafts, lessons, trackRecord }``. */
export async function getStandin(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  const qs = `?standin=me${opts.pool ? `&pool=${encodePath(opts.pool)}` : ''}`;
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/discussion${qs}`);
}

/** CONFIRM A PENDING ANSWER (STAND-IN.md §9) — ``decision``: approve | modify (``text`` = your wording) | discard.
 *  Only someone it waits for (its supervisor / the person a stand-in answered for). → ``{ message, learned? }``. */
export async function confirmAnswer(networkId, m, decision, text = '', opts = {}) {
  if (!networkId || !m || !m.id) throw new Error('networkId and message are required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/discussion`, {
    method: 'PATCH',
    body: JSON.stringify({ action: 'confirm-answer', messageId: m.id, sk: m.sk || '', decision, text,
      ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** MODEL CATALOG (MODEL-CATALOG.md §6) — recently added models this workspace doesn't use yet (OWNER/ADMIN; else []). */
export async function getModelNews() {
  return authedFetch('/data-plane/me/model-news');
}

/** REALTIME LANES (REALTIME-HUB.md §0.6) — ``{ stream: { httpDns, realtimeDns } }`` ('' = one-API mode). */
export async function getRealtimeConfig() {
  return authedFetch('/data-plane/me/realtime');
}

/** THE PROFILE TAB (STAND-IN.md §9) — ``{ away, rooms: [{networkId, poolId, setting, waiting, …}] }``. */
export async function getMyStandin() {
  return authedFetch('/data-plane/me/standin');
}

/** The master switch: ``away`` true = my stand-ins may answer (rooms keep their own switch); false = all paused. */
export async function setMyAway(away) {
  return authedFetch('/data-plane/me/standin', { method: 'PUT', body: JSON.stringify({ away: !!away }) });
}

/** STAND-IN GATES (STAND-IN.md §7) — every stand-in recommendation on a gate: ``{ reviews: [{forName, agentName,
 *  status: reviewing|advised|approved|followed|overruled, decision, confidence, basis, feedback}] }``. */
export async function getGateReviews(networkId, kind, gateId, opts = {}) {
  if (!networkId || !kind || !gateId) throw new Error('networkId, kind and gateId are required');
  const qs = `?standin=gate&kind=${encodePath(kind)}&gateId=${encodePath(gateId)}${opts.pool ? `&pool=${encodePath(opts.pool)}` : ''}`;
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/discussion${qs}`);
}

/** STAND-IN op — always MY own: ``set {agentId, enabled, threshold}`` | ``send {draftId, text?}`` | ``discard
 *  {draftId}`` | ``correct {messageId, sk, text}`` | ``forget {agentId, trigger}``. */
export async function standinOp(networkId, op, payload = {}, opts = {}) {
  if (!networkId || !op) throw new Error('networkId and op are required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/discussion`, {
    method: 'PATCH',
    body: JSON.stringify({ action: 'standin', op, ...payload, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** TEAM SIGNALS (MODES.md §3a): set / clear my ``agree`` | ``disagree`` | ``unclear`` on a message → ``{ message }``. */
export async function signalDiscussionMessage(networkId, m, signal, on, opts = {}) {
  if (!networkId || !m || !m.id) throw new Error('networkId and message are required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/discussion`, {
    method: 'PATCH',
    body: JSON.stringify({ action: 'react', messageId: m.id, sk: m.sk || '', signal, on: !!on, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

export async function deleteDiscussionMessage(networkId, m, opts = {}) {
  if (!networkId || !m || !m.id) throw new Error('networkId and message are required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/discussion`, {
    method: 'PATCH',
    body: JSON.stringify({ action: 'delete', messageId: m.id, sk: m.sk || '', ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** PROJECT KIND (G0, GAPS-AND-PREREQUISITES §3) — ``executable`` (agents do the work, the default) or
 *  ``plan_verify`` (people do the work; tasks complete on verified uploaded evidence). Shared per project;
 *  changeable until the first milestone is locked (409 after). Returns ``{ kind, kindLocked }``. */
export async function setProjectKind(networkId, kind, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/project-settings`, {
    method: 'PUT',
    body: JSON.stringify({ kind, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** TIMELINE SETTINGS (PLAN-TO-EXECUTE §5) — the project's SHARED schedule settings (planner scope, so
 *  everyone in the room(s) sees the same): ``{startDate?, targetDays?, milestone?, milestoneDays?}`` —
 *  a target of null clears it; fields not sent are unchanged. Returns ``{ schedule }``. */
export async function setProjectSchedule(networkId, patch, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/project-schedule`, {
    method: 'PUT',
    body: JSON.stringify({ ...(patch || {}), ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/** PLANNER RUNS (L60 §executions) — the async planner's durable run history for a room. The planner
 *  runs OUTSIDE the SFN (no Tasks row), so these records are how the Executions tab lists a "Planner"
 *  line per run (click → the persisted transcript via ``logKey``). Returns ``{ runs: [...] }``. */
export async function fetchPlannerRuns(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  const params = new URLSearchParams();
  if (opts.pool) params.set('pool', opts.pool);
  const qs = params.toString();
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/planner-runs${qs ? `?${qs}` : ''}`);
}

/** The per-task plan .md (§task-md) — rendered on demand from the ledger. Lazy: called only when the
 *  user clicks a task to view its file. Returns ``{ taskId, markdown }``. */
export async function fetchTaskMd(networkId, taskId, opts = {}) {
  if (!networkId || !taskId) throw new Error('networkId + taskId are required');
  const params = new URLSearchParams();
  params.set('taskId', taskId);
  if (opts.pool) params.set('pool', opts.pool);
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/task-md?${params.toString()}`);
}

/**
 * REALTIME HUB (REALTIME-HUB.md P0) — fetch the authoritative room SNAPSHOT ({seq, run, agents, plan}).
 * The room reconciles this on connect/focus/heartbeat instead of reducing the AppSync stream, so a
 * missed/early/reordered agent event can't corrupt the tree. Read-only + cheap (derive-on-read).
 */
// RELEASE ALARMS (RELEASE-ALARMS.md) — bake-time canary registry for the admin Alarms tab.
export async function listReleaseAlarms() {
  return authedFetch('/data-plane/alarms');
}

export async function configureReleaseAlarm(name, opts = {}) {
  if (!name) throw new Error('alarm name is required');
  return authedFetch(`/data-plane/alarms/${encodePath(name)}`, {
    method: 'PUT',
    body: JSON.stringify({
      ...(Number.isFinite(opts.threshold) ? { threshold: opts.threshold } : {}),
      ...(opts.reset ? { reset: true } : {}),
    }),
  });
}

// ROOM ALARMS (RELEASE-ALARMS.md §Rooms) — this room GROUP's live integration-test alarms + tripped
// count (drives the in-room ``Alarm (N)`` tab; 0 ⇒ tab hidden) and operator reset.
export async function fetchRoomAlarms(networkId, poolId) {
  if (!networkId || !poolId) throw new Error('networkId + poolId are required');
  const params = new URLSearchParams({ pool: poolId });
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-alarms?${params.toString()}`);
}

// UPGRADE QUALITY (UPGRADE-QUALITY.md §6) — this room group's post-upgrade units: status, verdicts
// (scores + evidence) and the sample vs the prior version's baseline, for the Compare blocks.
export async function fetchUpgradeQuality(networkId, poolId) {
  if (!networkId) throw new Error('networkId is required');
  const params = new URLSearchParams(poolId ? { pool: poolId } : {});
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/upgrade-quality?${params.toString()}`);
}

export async function resetRoomAlarm(networkId, poolId, name, opts = {}) {
  if (!networkId || !poolId || !name) throw new Error('networkId + poolId + name are required');
  const params = new URLSearchParams({ pool: poolId });
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-alarms?${params.toString()}`, {
    method: 'PUT',
    body: JSON.stringify({
      name,
      ...(Number.isFinite(opts.threshold) ? { threshold: opts.threshold } : {}),
      ...(opts.reset === false ? {} : { reset: true }),
    }),
  });
}

export async function fetchRoomState(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  const params = new URLSearchParams();
  if (opts.pool) params.set('pool', opts.pool);
  // 304 fast-path (REALTIME-HUB P1): pass the client's lastSeq — a settled, unchanged room answers
  // with a tiny ``{seq, notModified}`` body instead of the full snapshot.
  if (Number.isFinite(opts.since) && opts.since > 0) params.set('since', String(opts.since));
  const qs = params.toString();
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/room-state${qs ? `?${qs}` : ''}`);
}

// LANE-B log-tail backfill (REALTIME-HUB §0.1): builder LOG_LINEs stream ephemerally over AppSync, so a
// viewer opening the room mid-build (or reconnecting) sees a blank log. Fetch the bounded tail for a
// (run, agent) once on mount, render it, then live-tail — deduping live LOG_LINEs by ``lineNo`` > seq.
// Returns ``{seq, lines}`` (or ``{seq, notModified}`` when ``since`` >= seq).
export async function fetchBuilderLogTail(networkId, { pool, runId, agent, since } = {}) {
  if (!networkId || !runId || !agent) throw new Error('networkId, runId and agent are required');
  const params = new URLSearchParams();
  if (pool) params.set('pool', pool);
  params.set('runId', runId);
  params.set('agent', agent);
  if (Number.isFinite(since) && since > 0) params.set('since', String(since));
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/log-tail?${params.toString()}`);
}

export async function listProjectTasks(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  const params = new URLSearchParams();
  if (opts.view) params.set('view', opts.view);
  if (opts.milestone) params.set('milestone', opts.milestone);
  // §13 — the ledger is scoped to the room's project INSTANCE (a Lead anchors itself; a linked room
  // shares its Lead's). The backend resolves the scope from ``pool``, so the room UI MUST send its
  // poolId or the tab reads the wrong (empty) namespace. Omitted only for a network-level caller.
  if (opts.pool) params.set('pool', opts.pool);
  const qs = params.toString();
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/tasks${qs ? `?${qs}` : ''}`);
}

/**
 * Add one task to the plan (a milestone must exist + the lead agent must be real — validated
 * server-side via the amendment engine). 409 if the plan is already finalized.
 * @param {string} networkId
 * @param {{milestone:string, leadAgent:string, title:string, detail?:string,
 *   definitionOfDone?:string, involvedAgents?:string[], dependsOn?:string[]}} task
 */
export async function addProjectTask(networkId, task) {
  if (!networkId) throw new Error('networkId is required');
  // §13: the room's ``poolId`` (when present) rides IN the task object so the backend resolves the
  // room's INSTANCE ledger scope. Kept in the body — NOT a new positional arg — so the call arity is
  // unchanged (existing callers/tests that pass a bare task object are byte-identical).
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/tasks`, {
    method: 'POST',
    body: JSON.stringify(task || {}),
  });
}

/**
 * Enrich a draft task (detail / definitionOfDone / dependsOn / title). Only provided fields are
 * written. 409 if the plan is already finalized.
 */
export async function editProjectTask(networkId, taskId, patch) {
  if (!networkId || !taskId) throw new Error('networkId and taskId are required');
  // §13: the room's ``poolId`` (when present) rides IN the patch object — NOT a new positional arg —
  // so the call arity is unchanged (the amendment engine ignores unknown keys; the handler's
  // ``_planner_pool(event)`` reads body poolId to scope the write).
  return authedFetch(
    `/data-plane/networks/${encodePath(networkId)}/tasks/${encodePath(taskId)}`,
    { method: 'PATCH', body: JSON.stringify(patch || {}) },
  );
}

/**
 * MANUAL ATTESTATION (TOOL-READINESS §E) — close a task no tool can prove: flip it to ``done``
 * with a human note and/or uploaded evidence key. Rides the SAME PATCH route via ``action:attest``
 * (no new route). Requires a note OR evidenceKey. Distinct from a tool-verified completion.
 * @returns {Promise<{attested:boolean, taskId:string, closedBy:string}>}
 */
export async function attestProjectTask(networkId, taskId, { note = '', evidenceKey = '', pool = '' } = {}) {
  if (!networkId || !taskId) throw new Error('networkId and taskId are required');
  return authedFetch(
    `/data-plane/networks/${encodePath(networkId)}/tasks/${encodePath(taskId)}`,
    { method: 'PATCH', body: JSON.stringify({ action: 'attest', note, evidenceKey, ...(pool ? { poolId: pool } : {}) }) },
  );
}

/**
 * Finalize the plan — flip every DRAFT task to PENDING (runnable) + open the gate so worker rooms
 * may execute. Pass ``{finalized:false}`` to REOPEN for editing (closes the gate).
 * @returns {Promise<{finalized:boolean, promoted?:number}>}
 */
export async function finalizePlanning(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  // LOCK (P5a): ``opts.milestone`` locks ONE milestone (gated: approved + every task ready, or
  // ``opts.acceptExceptions``); with ``finalized:false`` it unlocks. ``opts.pool`` scopes to the room's
  // instance ledger. No milestone → the legacy whole-plan finalize (API only; no UI).
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/tasks/finalize`, {
    method: 'POST',
    body: JSON.stringify({
      finalized: opts.finalized !== false,
      ...(opts.milestone ? { milestone: opts.milestone } : {}),
      // LOCK (P5a): "Lock anyway" — accept the listed readiness exceptions (recorded on the lock).
      ...(opts.acceptExceptions ? { acceptExceptions: true } : {}),
      ...(opts.pool ? { poolId: opts.pool } : {}),
    }),
  });
}

/**
 * Approve a MILESTONE's plan (MILESTONE-SCOPED-PLANNING §2.5) — advances the PLANNING cursor to the
 * next milestone so the next plan run scopes to it. Distinct from ``finalizePlanning`` (which opens
 * EXECUTION). Pass ``{approved:false}`` to REOPEN a milestone for re-planning (rolling-wave).
 * @returns {Promise<{milestone:string, approved:boolean, approvedMilestones:string[], planningCursor:string}>}
 */
export async function approveMilestonePlan(networkId, milestone, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  if (!milestone) throw new Error('milestone is required');
  // ``opts.pool`` = the ROOM pool (e.g. "willow"), so the backend can fire a PLAN_UPDATED beacon on
  // that room's channel (§2.5g) → other members' Tasks tabs update live. The plan itself lives on
  // __planner__; the pool here is only the live-notify target. Omitted → no cross-member beacon.
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/tasks/approve-plan`, {
    method: 'POST',
    body: JSON.stringify({ milestone, approved: opts.approved !== false, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/**
 * Stop a running WAVE AUTO-SEQUENCE (MILESTONE-SCOPED-PLANNING §wave-auto) — clears the milestone's
 * AUTOWAVE marker so the final reducer fires no further waves (an in-flight wave finishes but won't
 * advance). Idempotent.
 * @returns {Promise<{milestone:string, autoWaveStopped:boolean}>}
 */
export async function stopAutoWavePlan(networkId, milestone, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  if (!milestone) throw new Error('milestone is required');
  // §13: ``opts.pool`` (when present) scopes to the room's instance ledger. Object arg, not a new
  // positional, so the call arity is stable.
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/tasks/stop-auto-plan`, {
    method: 'POST',
    body: JSON.stringify({ milestone, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/**
 * HOLISTIC RECONCILE (PLAN-DRIFT §reconcile): drain the approved-milestone gap backlog once every
 * milestone is approved. ``{apply:true}`` authors coarse reconciliation drafts + resolves the gaps;
 * omit ``apply`` for a preview (returns the backlog without writing). 400s if not fully approved.
 *
 * @param {string} networkId
 * @param {{pool?: string, apply?: boolean}} [opts]
 */
export async function reconcilePlan(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/tasks/reconcile`, {
    method: 'POST',
    body: JSON.stringify({ ...(opts.pool ? { poolId: opts.pool } : {}), ...(opts.apply ? { apply: true } : {}) }),
  });
}

/**
 * GAP AGENT proposal (PLAN-INTEGRATION §3.6): Accept (re-proven, then applied to the approved milestone)
 * or Dismiss ONE verified proposal. Same route as reconcile, keyed by ``gapId`` — any time, no
 * all-approved gate. Returns ``{gapId, ok, closed?, stale?, dismissed?}``.
 *
 * @param {string} networkId
 * @param {string} gapId
 * @param {'accept'|'dismiss'} action
 * @param {{pool?: string}} [opts]
 */
export async function resolvePlanProposal(networkId, gapId, action = 'accept', opts = {}) {
  if (!networkId || !gapId) throw new Error('networkId and gapId are required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/tasks/reconcile`, {
    method: 'POST',
    body: JSON.stringify({ gapId, action, ...(opts.pool ? { poolId: opts.pool } : {}) }),
  });
}

/**
 * Room Context action: ``estimate`` (FREE — projected points cost, no model
 * call) or ``synthesize`` (SPENDS — one Haiku call, caches the result with its
 * extraction date). The UI always estimates → confirms → synthesizes.
 *
 * @param {string} networkId
 * @param {{ pool?: string, action?: 'estimate'|'synthesize' }} opts
 */
export async function postRoomContext(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(
    `/data-plane/networks/${encodePath(networkId)}/room-context`,
    {
      method: 'POST',
      body: JSON.stringify({
        pool: opts.pool || undefined,
        action: opts.action || 'estimate',
      }),
    },
  );
}

/**
 * Network context (consolidated across all rooms) — the reporting view + the
 * button-state flags. ``eligible`` = network released with rooms; ``stale`` =
 * re-extraction would yield something new.
 *
 * @param {string} networkId
 * @returns {Promise<{eligible:boolean, roomCount:number, summary:?object, stale:boolean}>}
 */
export async function getNetworkContext(networkId) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/network-context`);
}

/**
 * Extract (consolidate + synthesize) the network-wide context on demand. SPENDS
 * one Haiku call — but no-ops (``status: not_stale``) when no room changed since
 * the last extract, unless ``force``. Gated server-side on released-with-rooms.
 *
 * @param {string} networkId
 * @param {{ force?: boolean }} [opts]
 */
export async function extractNetworkContext(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(
    `/data-plane/networks/${encodePath(networkId)}/network-context-extract`,
    { method: 'POST', body: JSON.stringify({ force: !!opts.force }) },
  );
}

/**
 * Skills-as-context tags (PLAN-INTEGRATION.md §2.6b) — which skills each agent carries
 * for worker/API-mode runs. Architect-seeded, user-editable on the Context tab.
 * @param {string} networkId
 * @returns {Promise<{skills: Array<{skillId,name}>, tagsByAgent: Record<string,string[]>}>}
 */
export async function getNetworkSkillTags(networkId) {
  if (!networkId) throw new Error('networkId is required');
  return authedFetch(`/data-plane/networks/${encodePath(networkId)}/skill-tags`);
}

/**
 * Set ONE agent's skill tags (tag/untag). User override.
 * @param {string} networkId
 * @param {string} agentId
 * @param {string[]} skillIds
 */
export async function setNetworkSkillTags(networkId, agentId, skillIds) {
  if (!networkId) throw new Error('networkId is required');
  if (!agentId) throw new Error('agentId is required');
  return authedFetch(
    `/data-plane/networks/${encodePath(networkId)}/skill-tags`,
    { method: 'POST', body: JSON.stringify({ agentId, skillIds: skillIds || [] }) },
  );
}

/**
 * Toggle a skill's network-wide HOLD (the Skill lane's Hold ↔ Skill switch).
 * ``held=true`` ⇒ the skill stops injecting for every agent (even where
 * subscribed); ``false`` ⇒ it injects again for its subscribers.
 * @param {string} networkId
 * @param {string} skillId
 * @param {boolean} held
 */
export async function setNetworkSkillHold(networkId, skillId, held) {
  if (!networkId) throw new Error('networkId is required');
  if (!skillId) throw new Error('skillId is required');
  return authedFetch(
    `/data-plane/networks/${encodePath(networkId)}/skill-tags`,
    { method: 'POST', body: JSON.stringify({ skillId, held: !!held }) },
  );
}

/**
 * Prompt capture (doc/arch/CONTEXT.md §13b) — fetch the fully-assembled prompt
 * an agent was given for a run, for the room's "Context" link. Breaks the
 * black-box effect (see exactly what the agent was told: identity, team
 * composition, role contract, tool specs, injected memory/room-context) and
 * helps fine-tune prompts. Read-only + RBAC-gated server-side.
 *
 * Worker source is keyed by (pool, agent, task); builder source by (run, agent).
 * Resolves to ``null`` on 404 (no capture yet — run hasn't reached
 * prompt-assembly, capture was best-effort-skipped, or it aged out at 30 days).
 *
 * @param {string} networkId
 * @param {{ source?: 'worker'|'builder', agent: string, pool?: string, task?: string, run?: string }} opts
 * @returns {Promise<{source:string, capture:object}|null>}
 */
export async function getPromptCapture(networkId, opts = {}) {
  if (!networkId) throw new Error('networkId is required');
  if (!opts.agent) throw new Error('agent is required');
  const params = new URLSearchParams();
  params.set('source', opts.source || 'worker');
  params.set('agent', opts.agent);
  if (opts.pool) params.set('pool', opts.pool);
  if (opts.task) params.set('task', opts.task);
  if (opts.run) params.set('run', opts.run);
  try {
    return await authedFetch(
      `/data-plane/networks/${encodePath(networkId)}/prompt-capture?${params.toString()}`,
    );
  } catch (err) {
    // 404 = no capture for this run yet; treat as a soft "nothing to show".
    if (err && (err.status === 404 || /not found/i.test(String(err.message || '')))) {
      return null;
    }
    throw err;
  }
}

export const _internal = { authedFetch, encodePath, API_BASE_URL };
